import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useRiderArrive, useRiderHandover, useRiderRun } from '@/lib/consoleQueries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { AdminError } from '@/features/admin/AdminError';

/**
 * The door. The most consequential screen in ChiCare.
 *
 * ── DESIGNED FOR ONE THUMB, IN SUNLIGHT, IN A HURRY ─────────────────────────
 *
 * A rider is standing at a gate holding a sealed bag. Type is large, targets
 * are tall, and there is exactly one thing to do at a time. Anything that could
 * be read later is not on this screen.
 *
 * ── §9.21: THE CODE ENTRY DOES NOT EXIST UNTIL THE MONEY HAS MOVED ──────────
 *
 * On a door-capture order the goods are still ChiCare's problem until the card
 * clears. So the code field is not disabled — it is ABSENT — and in its place is
 * a plain instruction not to hand over the bag. A disabled field invites
 * tapping; a missing one ends the question.
 *
 * The server decides this. `ready_for_handover` comes from the API and is never
 * computed here, because an old build with a stale rule would hand over
 * medicine against an uncharged card.
 *
 * ── WHY THE CODE IS SIX SEPARATE THOUGHTS, NOT ONE FIELD ────────────────────
 *
 * The buyer reads it aloud, digit by digit, usually over traffic. A numeric
 * keypad and a large monospace field mean the rider can glance down, confirm
 * what they typed, and look back up — which is what actually happens at a door.
 */
export function HandoverScreen() {
  const { reference = '' } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useRiderRun(reference || null);
  const arrive = useRiderArrive();
  const handover = useRiderHandover();

  const [code, setCode] = useState('');
  const [recipient, setRecipient] = useState('');

  if (isError) {
    return <AdminError title="We couldn't load this delivery" error={error} onRetry={() => refetch()} />;
  }

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-[560px] space-y-4 px-4 py-6">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-40 w-full" radius="card" />
      </div>
    );
  }

  const { run, doorCheckSeconds, note } = data;

  if (handover.isSuccess || run.delivered) {
    return <Handed reference={run.reference} onDone={() => navigate('/rider')} />;
  }

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-5">
      <button
        type="button"
        onClick={() => navigate('/rider')}
        className="mb-4 inline-flex min-h-11 items-center gap-1.5 bg-transparent p-0 text-[13px] font-medium text-muted"
      >
        <Icon name="back" size={15} />
        All runs
      </button>

      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
        {run.deliver_to?.recipient_name ?? 'Delivery'}
      </h1>
      <p className="m-0 mt-1 font-mono text-[12.5px] text-muted">{run.reference}</p>

      {/* Address and phone first — this is what gets the rider to the door. */}
      <section className="mt-4 rounded-card border border-line bg-white p-4">
        <p className="m-0 text-[14px] leading-relaxed text-ink">
          {[
            run.deliver_to?.line1,
            run.deliver_to?.line2,
            run.deliver_to?.lga,
            run.deliver_to?.state,
          ]
            .filter(Boolean)
            .join(', ')}
        </p>

        {run.deliver_to?.phone ? (
          <a
            href={`tel:${run.deliver_to.phone}`}
            className="mt-3 flex min-h-12 items-center justify-center gap-2 rounded-btn bg-blue text-[15px] font-bold text-white no-underline"
          >
            <Icon name="phone" size={17} />
            Call {run.deliver_to.phone}
          </a>
        ) : null}
      </section>

      {/* The seal is the custody chain. Checking it is the first thing that
          happens at the door, so it sits above everything else. */}
      {run.seal_number ? (
        <section className="mt-3 rounded-card border border-line bg-white p-4">
          <h2 className="m-0 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
            Check the seal matches
          </h2>
          <p className="m-0 mt-1 font-mono text-[20px] font-bold tracking-[0.06em] text-ink">
            {run.seal_number}
          </p>
          <p className="m-0 mt-1.5 text-[12.5px] leading-relaxed text-muted">
            If the seal is broken or the number is different, do not hand it over. Call support.
          </p>
        </section>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Badge tone="neutral">{run.item_count} item{run.item_count === 1 ? '' : 's'}</Badge>
        {run.has_cold_chain ? <Badge tone="brand">Cold chain</Badge> : null}
        <span className="font-mono text-[12.5px] text-muted">{run.value_display}</span>
      </div>

      {run.awaiting_payment ? (
        <PaymentPending onArrive={() => arrive.mutate(run.reference)} pending={arrive.isPending} />
      ) : !run.ready_for_handover ? (
        <section
          data-testid="handover-locked"
          className="mt-4 rounded-card border border-caution bg-caution-tint p-4"
        >
          <p className="m-0 text-[14px] font-bold text-ink">Do not hand over the bag yet</p>
          <p className="m-0 mt-1 text-[13px] leading-relaxed text-ink">{note}</p>
        </section>
      ) : (
        <CodeEntry
          code={code}
          setCode={setCode}
          recipient={recipient}
          setRecipient={setRecipient}
          doorCheckSeconds={doorCheckSeconds}
          pending={handover.isPending}
          error={handover.error}
          onSubmit={() =>
            handover.mutate({
              reference: run.reference,
              code,
              recipientName: recipient.trim() || undefined,
            })
          }
        />
      )}
    </div>
  );
}

/**
 * A door capture that has not cleared.
 *
 * The rider presses "I'm at the door", which is what fires the charge. Until it
 * clears there is deliberately nothing else to press.
 */
function PaymentPending({ onArrive, pending }: { onArrive: () => void; pending: boolean }) {
  return (
    <section
      data-testid="awaiting-payment"
      className="mt-4 rounded-card border border-caution bg-caution-tint p-4"
    >
      <p className="m-0 text-[14px] font-bold text-ink">Payment is still going through</p>
      <p className="m-0 mt-1 text-[13px] leading-relaxed text-ink">
        This order is paid at the door. Tap below when you have arrived — the code entry appears as
        soon as the payment clears. Keep hold of the bag until then.
      </p>
      <Button size="lg" className="mt-3 w-full" disabled={pending} onClick={onArrive}>
        {pending ? 'Checking…' : "I'm at the door"}
      </Button>
    </section>
  );
}

function CodeEntry({
  code,
  setCode,
  recipient,
  setRecipient,
  doorCheckSeconds,
  pending,
  error,
  onSubmit,
}: {
  code: string;
  setCode: (value: string) => void;
  recipient: string;
  setRecipient: (value: string) => void;
  doorCheckSeconds: number;
  pending: boolean;
  error: unknown;
  onSubmit: () => void;
}) {
  return (
    <section data-testid="code-entry" className="mt-4 rounded-card border border-line bg-white p-4">
      <h2 className="m-0 font-display text-[16px] font-bold tracking-[-0.01em]">
        Let them check the pack first
      </h2>
      <p className="m-0 mt-1 text-[13px] leading-relaxed text-muted">
        Open the bag with them. They have about {Math.round(doorCheckSeconds / 60)} minutes to look
        at what is inside. If anything is wrong it comes back with you — do not take the code.
      </p>

      <label className="mt-4 block">
        <span className="mb-2 block text-[13px] font-bold text-ink">
          Now ask for their delivery code
        </span>
        <input
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 8))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          aria-label="Delivery code"
          className="min-h-[64px] w-full rounded-btn border-2 border-line bg-paper px-4 text-center font-mono text-[32px] font-bold tracking-[0.22em] text-ink outline-none focus:border-blue"
        />
      </label>

      <label className="mt-3 block">
        <span className="mb-1.5 block text-[12.5px] font-medium text-muted">
          Who took it? (optional)
        </span>
        <input
          value={recipient}
          onChange={(event) => setRecipient(event.target.value)}
          placeholder="Name of the person at the door"
          className="min-h-12 w-full rounded-btn border border-line bg-paper px-3 text-[14px] text-ink outline-none focus:border-blue"
        />
      </label>

      {error ? (
        <p role="alert" className="mt-3 text-[13px] font-medium leading-relaxed text-danger">
          {error instanceof Error ? error.message : 'That code did not work.'}
        </p>
      ) : null}

      <Button
        size="lg"
        className="mt-4 w-full"
        disabled={pending || code.length < 4}
        onClick={onSubmit}
      >
        {pending ? 'Checking…' : 'Confirm handover'}
      </Button>
    </section>
  );
}

function Handed({ reference, onDone }: { reference: string; onDone: () => void }) {
  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-10 text-center">
      <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full bg-verified-tint">
        <Icon name="check" size={30} className="text-verified" />
      </div>

      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">Handed over</h1>
      <p className="mx-auto mt-2 max-w-[36ch] text-[13.5px] leading-relaxed text-muted">
        Recorded against <span className="font-mono">{reference}</span>. Wait a moment in case they
        spot something — after that the order is confirmed on its own.
      </p>

      <Button size="lg" className={cn('mt-5 w-full')} onClick={onDone}>
        Next delivery
      </Button>
    </div>
  );
}
